import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { KeyRound, Loader2, Palette, UserCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Btn, Card, PageHeader } from "@/components/dashboard/Ui";

const COLORS = ["#2f5d83", "#087f8c", "#3e9b54", "#4b6a7a", "#2b7a9a", "#6a8f3a", "#b08a2a", "#c2493a"];

export default function SettingsPage() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [color, setColor] = useState(user?.color || COLORS[0]);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });

  const profile = useMutation({
    mutationFn: async () => (await api.patch("/users/me/profile", { name, color })).data,
    onSuccess: (res) => {
      setUser(res.user);
      toast.success("Profile updated");
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const password = useMutation({
    mutationFn: async () => (await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next })).data,
    onSuccess: () => {
      toast.success("Password changed");
      setPw({ current: "", next: "", confirm: "" });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <div data-testid="settings-page">
      <PageHeader title="Settings" description="Your profile and sign-in details." />
      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="p-6">
          <h2 className="flex items-center gap-2 text-lg font-semibold">
            <UserCircle2 className="h-5 w-5 text-primary" /> Profile
          </h2>
          <form
            className="mt-4 space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              profile.mutate();
            }}
          >
            <div>
              <label htmlFor="p-name" className="mb-1.5 block text-sm font-semibold">Display name</label>
              <input id="p-name" className="emerge-input !h-10 text-sm" value={name} onChange={(e) => setName(e.target.value)} minLength={2} required data-testid="settings-name-input" />
            </div>
            <div>
              <p className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold">
                <Palette className="h-4 w-4" /> Calendar colour
              </p>
              <div className="flex flex-wrap gap-2">
                {COLORS.map((c) => (
                  <button key={c} type="button" onClick={() => setColor(c)} className={`h-9 w-9 rounded-full border-2 ${color === c ? "border-foreground" : "border-transparent"}`} style={{ backgroundColor: c }} aria-label={`Colour ${c}`} aria-pressed={color === c} data-testid="settings-color-swatch" />
                ))}
              </div>
            </div>
            <dl className="grid grid-cols-2 gap-2 rounded-[10px] bg-secondary/60 p-3 text-sm">
              <dt className="text-muted-foreground">Email</dt>
              <dd className="font-medium">{user?.email}</dd>
              <dt className="text-muted-foreground">Username</dt>
              <dd className="font-medium">{user?.username || "-"}</dd>
              <dt className="text-muted-foreground">Role</dt>
              <dd className="font-medium capitalize">{user?.role}</dd>
            </dl>
            <Btn type="submit" disabled={profile.isPending} data-testid="settings-save-profile-button">
              {profile.isPending && <Loader2 className="animate-spin" />} Save profile
            </Btn>
          </form>
        </Card>

        <Card className="p-6">
          <h2 className="flex items-center gap-2 text-lg font-semibold">
            <KeyRound className="h-5 w-5 text-primary" /> Change password
          </h2>
          <form
            className="mt-4 space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (pw.next !== pw.confirm) {
                toast.error("New passwords do not match");
                return;
              }
              password.mutate();
            }}
          >
            <div>
              <label htmlFor="pw-current" className="mb-1.5 block text-sm font-semibold">Current password</label>
              <input id="pw-current" type="password" className="emerge-input !h-10 text-sm" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} required autoComplete="current-password" data-testid="settings-current-password-input" />
            </div>
            <div>
              <label htmlFor="pw-next" className="mb-1.5 block text-sm font-semibold">New password</label>
              <input id="pw-next" type="password" className="emerge-input !h-10 text-sm" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} required minLength={8} autoComplete="new-password" data-testid="settings-new-password-input" />
              <p className="mt-1 text-xs text-muted-foreground">At least 8 characters with a letter and a number.</p>
            </div>
            <div>
              <label htmlFor="pw-confirm" className="mb-1.5 block text-sm font-semibold">Confirm new password</label>
              <input id="pw-confirm" type="password" className="emerge-input !h-10 text-sm" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} required autoComplete="new-password" data-testid="settings-confirm-password-input" />
            </div>
            <Btn type="submit" variant="secondary" disabled={password.isPending} data-testid="settings-change-password-button">
              {password.isPending && <Loader2 className="animate-spin" />} Update password
            </Btn>
          </form>
        </Card>
      </div>
    </div>
  );
}
